import React from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  Typography,
} from '@mui/material';
import {
  Dashboard,
  Add,
  Inventory2,
  LocationOn,
  Undo,
  ImageSearch,
  FactCheck,
  TableChart,
  VpnKey,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { usePermission } from '../../hooks/usePermission';
import { FeatureKey } from '../../rbac/features';
import packageJson from '../../../package.json';

const EASE = [0.16, 1, 0.3, 1] as const;


interface SidebarProps {
  open: boolean;
  onClose: () => void;
  width?: number;
}

interface MenuItem {
  text: string;
  icon: React.ReactElement;
  path: string;
  description?: string;
  // RBAC feature gating this item's visibility (view+). Omitted = always visible
  // (Dashboard/home is reachable by any authenticated user).
  feature?: FeatureKey;
}

const BASE_MENU_ITEMS: MenuItem[] = [
  {
    text: 'Dashboard',
    icon: <Dashboard />,
    path: '/',
    description: 'Overview and quick stats',
  },
  {
    text: 'Create Bundle',
    icon: <Add />,
    path: '/create-bundle',
    description: 'Start bundle creation process',
    feature: 'add-bundle',
  },
  {
    text: 'Debundle',
    icon: <Undo />,
    path: '/debundle',
    description: 'Break down bundles to child SKUs',
    feature: 'reduce-bundle',
  },
  {
    text: 'Inventory Status',
    icon: <Inventory2 />,
    path: '/inventory',
    description: 'Current stock levels and alerts',
    feature: 'view-inventory',
  },
  {
    text: 'Shelf Inventory',
    icon: <LocationOn />,
    path: '/inventory-shelf',
    description: 'Search inventory by SKU and shelf location',
    feature: 'view-inventory',
  },
  {
    text: 'Bundle Catalog',
    icon: <TableChart />,
    path: '/bundle-categories?view=bundle',
    description: 'GB/Bundle view and Component view tables',
    feature: 'bundle-skus',
  },
  {
    text: 'Cyclic Count',
    icon: <FactCheck />,
    path: '/cyclic-count',
    description: 'Physical inventory count and variance adjustment',
    feature: 'cyclic-count',
  },
  // Removed Bundles/Reports/History entries per request
];


const Sidebar: React.FC<SidebarProps> = ({ open, onClose, width = 280 }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const reduceMotion = useReducedMotion();

  // Per-feature view capability for the nav items (hooks must be called unconditionally).
  const canView: Record<string, boolean> = {
    'add-bundle': usePermission('add-bundle').can('view'),
    'reduce-bundle': usePermission('reduce-bundle').can('view'),
    'view-inventory': usePermission('view-inventory').can('view'),
    'bundle-skus': usePermission('bundle-skus').can('view'),
    'cyclic-count': usePermission('cyclic-count').can('view'),
    'image-generation': usePermission('image-generation').can('view'),
  };
  const canManageUsers = usePermission('manage-users').can('view');
  // Access Control is an admin-only drill-down; gate it at access-control EDIT so its
  // visibility matches the route guard in App.tsx (Admin-only in practice).
  const canViewAccessControl = usePermission('access-control').can('edit');

  const allMenuItems: MenuItem[] = [
    ...BASE_MENU_ITEMS,
    {
      text: 'Image Generation',
      icon: <ImageSearch />,
      path: '/image-generation',
      description: 'Generate AI product photography composites',
      feature: 'image-generation',
    },
  ];
  // A hidden feature's item is dropped; items without a feature (Dashboard) always show.
  const menuItems = allMenuItems.filter((item) => !item.feature || canView[item.feature]);

  const handleNavigation = (path: string) => {
    navigate(path);
    onClose();
  };

  return (
    <Drawer
      variant="temporary"
      open={open}
      onClose={onClose}
      sx={{
        width: width,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: width,
          boxSizing: 'border-box',
          bgcolor: 'background.paper',
          borderRight: '1px solid',
          borderColor: 'grey.200',
        },
      }}
    >
      {/* Header Section */}
      <Box
        sx={{
          p: 3,
          background: 'linear-gradient(135deg, #1A1410 0%, #3A2A1E 100%)',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            right: 0,
            width: '160px',
            height: '160px',
            background: 'radial-gradient(circle, rgba(220, 88, 41, 0.45) 0%, rgba(220, 88, 41, 0) 70%)',
            borderRadius: '50%',
            transform: 'translate(40px, -40px)',
          },
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, position: 'relative', zIndex: 1 }}>
          Bundle Manager
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.9, position: 'relative', zIndex: 1 }}>
          Warehouse Management
        </Typography>
      </Box>

      <Divider sx={{ borderColor: 'grey.200' }} />

      {/* Navigation Menu */}
      <List sx={{ pt: 2, px: 2 }}>
        {menuItems.map((item, index) => (
          <ListItem
            key={item.text}
            disablePadding
            sx={{ mb: 1 }}
            component={motion.li}
            initial={reduceMotion ? false : { opacity: 0, x: -12 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3, ease: EASE, delay: reduceMotion ? 0 : Math.min(index, 10) * 0.04 }}
          >
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => handleNavigation(item.path)}
              sx={{
                borderRadius: 2,
                py: 1.5,
                px: 2,
                transition: 'all 0.2s ease-in-out',
                '&.Mui-selected': {
                  background: 'linear-gradient(135deg, #DC5829 0%, #B8451E 100%)',
                  color: 'white',
                  boxShadow: '0px 4px 12px rgba(220, 88, 41, 0.3)',
                  '& .MuiListItemIcon-root': {
                    color: 'white',
                  },
                  '& .MuiListItemText-secondary': {
                    color: 'rgba(255, 255, 255, 0.8)',
                  },
                  '&:hover': {
                    background: 'linear-gradient(135deg, #F26A2E 0%, #DC5829 100%)',
                  },
                },
                '&:not(.Mui-selected):hover': {
                  bgcolor: 'grey.50',
                  transform: reduceMotion ? 'none' : 'translateX(4px)',
                  '& .MuiListItemIcon-root': {
                    color: 'primary.main',
                    transform: reduceMotion ? 'none' : 'scale(1.1)',
                  },
                },
              }}
            >
              <ListItemIcon
                sx={{
                  color: location.pathname === item.path
                    ? 'inherit'
                    : 'text.secondary',
                  minWidth: 36,
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: location.pathname === item.path ? 600 : 500,
                      fontSize: '0.9rem',
                    }}
                  >
                    {item.text}
                  </Typography>
                }
                secondary={
                  <Typography
                    variant="caption"
                    sx={{
                      color: location.pathname === item.path
                        ? 'rgba(255, 255, 255, 0.8)'
                        : 'text.secondary',
                      fontSize: '0.75rem',
                      mt: 0.5,
                    }}
                  >
                    {item.description}
                  </Typography>
                }
              />
            </ListItemButton>
          </ListItem>
        ))}
        {/* Manage Users — shown on manage-users view capability */}
        {canManageUsers && (
          <ListItem disablePadding sx={{ mb: 1 }}>
            <ListItemButton
              selected={location.pathname === '/admin/users'}
              onClick={() => handleNavigation('/admin/users')}
              sx={{ borderRadius: 2, py: 1.5, px: 2 }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <Add />
              </ListItemIcon>
              <ListItemText
                primary={<Typography variant="body2" sx={{ fontWeight: 500 }}>User Management</Typography>}
                secondary={<Typography variant="caption" sx={{ color: 'text.secondary' }}>Create and manage users</Typography>}
              />
            </ListItemButton>
          </ListItem>
        )}
        {/* Access Control — admin-only; gated on access-control edit (matches the route). */}
        {canViewAccessControl && (
          <ListItem disablePadding sx={{ mb: 1 }}>
            <ListItemButton
              selected={location.pathname === '/access-control'}
              onClick={() => handleNavigation('/access-control')}
              sx={{ borderRadius: 2, py: 1.5, px: 2 }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <VpnKey />
              </ListItemIcon>
              <ListItemText
                primary={<Typography variant="body2" sx={{ fontWeight: 500 }}>Access Control</Typography>}
                secondary={<Typography variant="caption" sx={{ color: 'text.secondary' }}>Last sign-in &amp; approximate location</Typography>}
              />
            </ListItemButton>
          </ListItem>
        )}
      </List>

      {/* Bottom Section */}
      <Box sx={{ mt: 'auto', p: 2 }}>
        <Box
          sx={{
            p: 2,
            bgcolor: 'grey.50',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'grey.200',
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
            Version {packageJson.version}
          </Typography>
          <Typography variant="body2" color="text.primary" sx={{ mt: 0.5, fontWeight: 500 }}>
            Bundle Management
          </Typography>
        </Box>
      </Box>
    </Drawer>
  );
};

export default Sidebar;